import {
  JourneyReviewTabs,
  ReviewType,
} from 'modules/workflows/components/JourneyReview/JourneyReview.constants';
import {
  addNodeErrors,
  getClonedFromJourneyInvalidNodes,
  getErrorNodes,
  getIsWorkflowSaving,
  getNodes,
  removeNodeError,
} from 'modules/workflows/ducks';
import { NodeError, useValidateWorkflow } from 'modules/workflows/hooks';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CompletedItem } from './JourneyReviewContext.types';
import { hydrateNodeReviewErrors } from './JourneyReviewContext.utils';

const ISSUE_TITLE = 'Tile setup incomplete';
const ISSUE_DESCRIPTION = 'Edit the tile and finish setting up the required fields.';

const CLONE_ISSUE_TITLE = 'Some copied fields could not be matched from the original project';
const CLONE_ISSUE_DESCRIPTION = `Some of this tile's settings could not be copied from the previous project. Update the highlighted fields to reference the current project's data and resources.`;

const uniqueNodeIds = (nodeErrors: NodeError[]) => [
  ...new Set(nodeErrors.map((x: NodeError) => x?.data?.nodeId.toString())),
];

export const useJourneyIssues = ({ activeTab }: { activeTab: JourneyReviewTabs }) => {
  const [issueIds, setIssueIds] = useState<string[]>([]);
  const [completed, setCompleted] = useState<CompletedItem[]>([]);
  const isJourneySaving = useSelector(getIsWorkflowSaving);
  const dispatch = useDispatch();
  const errorNodes = useSelector(getErrorNodes);
  const nodes = useSelector(getNodes);

  const errorClonedNodesIds = useSelector(getClonedFromJourneyInvalidNodes);

  const onValidationSuccess = () => {
    dispatch(addNodeErrors([]));
    setIssueIds([]);
  };

  const onValidationError = (data: NodeError[]) => {
    const nodeIds = uniqueNodeIds(data);
    if (activeTab === JourneyReviewTabs.Issues) {
      dispatch(addNodeErrors(nodeIds));
    } else {
      dispatch(addNodeErrors(nodeIds, false));
      setIssueIds(nodeIds);
    }
  };

  const {
    validateWorkflow,
    isLoading: isIssuesLoading,
    hasError: hasIssuesError,
  } = useValidateWorkflow({ onValidationError, onValidationSuccess });

  const setCachedIssuesOnStore = () => {
    dispatch(addNodeErrors(issueIds));
    setIssueIds([]);
  };

  const setIssuesToCache = () => {
    const errorTileIds = errorNodes.map((x) => x.id);
    setIssueIds(errorTileIds);
    dispatch(addNodeErrors(errorTileIds, false));
  };

  const onOptimizationComplete = (tileIds: string[]) => {
    const filteredTileIds = tileIds.filter((id) => nodes.some((node) => node.id === id));

    // remove the issueIds that match the filteredTileIDs
    const updatedIssueIds = issueIds.filter((id) => !filteredTileIds.includes(id));

    setIssueIds(updatedIssueIds);
  };

  const incompleteIssuesCount = errorNodes.length || issueIds.length;

  const issues = useMemo(() => {
    const incompleteIds = errorNodes
      .map((x) => x.id)
      .filter((x) => !errorClonedNodesIds.some((y) => y === x));

    const completedIssueIds = completed
      .filter((x) => x.reviewType === ReviewType.Issue)
      .map((x) => x.id);

    const hydratedIssues = hydrateNodeReviewErrors(
      nodes,
      incompleteIds,
      completedIssueIds,
      ReviewType.Issue,
      ISSUE_TITLE,
      ISSUE_DESCRIPTION,
    );

    const completedCloneIds = completed
      .filter((x) => x.reviewType === ReviewType.CloneIssue)
      .map((x) => x.id);

    const hydratedClonedIssues = hydrateNodeReviewErrors(
      nodes,
      errorClonedNodesIds,
      completedCloneIds,
      ReviewType.CloneIssue,
      CLONE_ISSUE_TITLE,
      CLONE_ISSUE_DESCRIPTION,
    );

    return [...hydratedIssues, ...hydratedClonedIssues].sort((a, b) =>
      a.tileId.localeCompare(b.tileId),
    );
  }, [nodes, errorNodes, completed, errorClonedNodesIds]);

  const completeIssue = (tileId: string) => {
    const reviewType = errorClonedNodesIds.includes(tileId)
      ? ReviewType.CloneIssue
      : ReviewType.Issue;
    if (errorNodes.some((x) => x.id === tileId)) {
      setCompleted((completedItems) => [...completedItems, { id: tileId, reviewType }]);
      dispatch(removeNodeError());
    }
  };

  const clearCompletedIssues = () => {
    setCompleted([]);
  };

  const handleSetIssues = (data: NodeError[]) => {
    clearCompletedIssues();
    const nodeIds = uniqueNodeIds(data);
    dispatch(addNodeErrors(nodeIds));
  };

  const handleValidateJourney = () => {
    clearCompletedIssues();
    dispatch(
      addNodeErrors(
        errorNodes.map((x) => x.id),
        false,
      ),
    );
    validateWorkflow();
  };

  return {
    validateJourney: handleValidateJourney,
    onOptimizationComplete,
    issues,
    isIssuesLoading: !!isJourneySaving || !!isIssuesLoading,
    hasIssuesError,
    incompleteIssuesCount,
    completeIssue,
    clearCompletedIssues,
    setCachedIssuesOnStore,
    setIssuesToCache,
    setIssues: handleSetIssues,
  };
};
